/**
 * Product option component
 * 
 * @typedef {object} Refs
 *
 * @extends {Component<Refs>}
 */
class ProductOption extends HTMLElement {

  // Predefined order for carat weight string values
  static CARAT_WEIGHT_ORDER = ['Small', 'Medium', 'Large', 'Extra Large', 'Extra-Large'];

  // How many grid rows stay visible while the options are collapsed
  static MAX_VISIBLE_ROWS = 3;

  static get observedAttributes() {
    return ['columns', 'handle', 'tags', 'image-size', 'product-handle', 'label-id'];
  }

  get columns() {
    return this.getAttribute('columns');
  }

  set columns(value) {
    this.setAttribute('columns', value);
  }

  get handle() {
    return this.getAttribute('handle');
  }

  get tags() {

    // Remove null values and return the tags
    return this.getAttribute('tags').split(' | ').filter(tag => (!tag.includes(': null') && !tag.includes(this.handleTag)));
  }

  set tags(value) {
    this.setAttribute('tags', value);
  }

  get handleTag() {
    return this.getAttribute('handle') + ':';
  }

  get imageSize() {
    return this.getAttribute('image-size');
  }

  get activeOption() {
    return this.getAttribute('active-option') ? this.handleize(this.getAttribute('active-option')) : null;
  }

  get productHandle() {
    return this.getAttribute('product-handle');
  }

  get groupTag() {
    return this.getAttribute('group-tag');
  }

  get labelId() {
    // Use provided label-id if available, otherwise generate from handle
    return this.getAttribute('label-id') || `option-name-${this.handle}`;
  }
  

  constructor() {
    super();

    // Check if group tag is set
    if (!this.groupTag) {
      console.warn('Group tag is not set');
      return;
    }
  }

  connectedCallback() {

    // TODO: Remove this once we find a better way to handle this logic in the products
    if (this.handle === 'product-center-stone-name') {
      
      // Remove tag that contains diamond-quality
      let newTags = this.tags.filter(tag => !tag.includes('diamond-quality'));
      this.tags = newTags.join(' | ');
    }
    
    theme.product.getProductHandlesByTag(this.tags).then(products => {

      // If options count is less than columns count, set columns count to options count
      if(products.length < this.columns) {
          this.columns = products.length;
      }

      this.render(products);
      this.calcVisibleOptions();
      this.checkIfNeedsSeeMore();
      this.initEvents();

      this.classList.add('loaded');
    });
  }

  /**
   * Handleize the value
   * 
   * @param {string} value - The value to handleize
   * @returns 
   */
  handleize(value) {
    return value.toLowerCase().replaceAll('  ', ' ').replaceAll('&', '').replaceAll(' ', '-').replaceAll('--', '-');
  }

  /**
   * Render the options
   * 
   * @param {*} products 
   */
  render(products) {

    let optionshtml = '';

    let optionCount = 0;

    // Track unique option handles to avoid duplicates
    const addedOptions = new Set();

    // TODO: Origin Natural or Lab Grown logic - Remove this once we find a better way to handle origin data in the products
    const isLabGrown = this.activeOption && this.activeOption.includes('lab');

    let originHandle = null;

    // Collect options first for carat-weight to enable sorting
    const optionsData = [];

    products.forEach(product => {

      // Check if product has group tag
      // TODO: Remove this once we find a better way to handle group tag data in the products using Shopify API. At this moment Shopify API has an issue to find products by tag with no - (dash) in the tag. For example, if we have a tag like "test: aaa", it will return products with tag "test: aaa-a" too.
      if (!product.tags.includes(this.groupTag)) {
        return;
      }

      const optionTag = product.tags.filter(tag => tag.includes(this.handleTag));
      
      if (optionTag.length <= 0) return;

      let label = optionTag[0].split(': ')[1];
      const originalLabel = label; // Store original label for sorting

      const optionHandle = this.handleize(label);

      // TODO: Origin Natural or Lab Grown logic - Remove this once we find a better way to handle origin data in the products
      if (isLabGrown && !label.toLowerCase().includes('lab')) {
        originHandle ??= product.handle;
        return;
      } else if (!isLabGrown && label.toLowerCase().includes('lab')) {
        originHandle ??= product.handle;
        return;
      }

      // Skip if this option has already been added
      if (addedOptions.has(optionHandle)) {
        return;
      }

      // Calculate numeric value for sorting (use original label before any formatting)
      let numericValue = null;
      let isStringValue = false;
      if (this.handle === 'carat-weight') {
        const parsed = parseFloat(originalLabel);
        if (!isNaN(parsed) && isFinite(parsed)) {
          numericValue = parsed;
        } else {
          // String value (e.g., "Small", "Large")
          isStringValue = true;
          numericValue = Infinity; // Put string values at the end when sorting
        }
      } else if (this.handle === 'width') {
        // Extract numeric value from width label (e.g., "3mm" -> 3)
        const match = originalLabel.match(/(\d+(?:\.\d+)?)/);
        numericValue = match ? parseFloat(match[1]) : null;
      }

      // TODO: Remove this once we find a better way to handle carat weight data in the products
      if (this.handle === 'carat-weight') {
        // Check if label is numeric before parsing and formatting
        const numericLabel = parseFloat(originalLabel);
        if (!isNaN(numericLabel) && isFinite(numericLabel)) {
          label = numericLabel.toFixed(2);
        }
        // If not numeric (e.g., "Small", "Large"), keep original label
      }

      let imageClass = '';

      if (this.imageSize) {
        imageClass = `option-image-${this.handle}-${optionHandle}`;

        // TODO: Remove this once we migrate product center stone name to gemstone name
        if (this.handle === 'product-center-stone-name') {
          imageClass = `option-image-gemstone-${optionHandle}`;
        }

        if (this.handle === 'product-center-stone-shape') {
          imageClass = `option-image-center-stone-shape-${optionHandle}`;
        }
      }

      // Colours have no metaobject image until the `color` metaobject is created,
      // so paint the swatch from the shared palette when no image is available.
      let swatchStyle = '';
      if (this.imageSize && this.handle === 'color') {
        const hasImage = Boolean(window.theme?.getColorSwatchImage?.(optionHandle));
        if (!hasImage) {
          const background = window.theme?.getColorSwatchBackground?.(optionHandle);
          swatchStyle = background
            ? `background: ${background};`
            : 'background: linear-gradient(135deg, #F1F1F1 0 50%, #D8D8D8 50% 100%);';
        }
      }

      // Store option data for sorting if needed
      optionsData.push({
        productHandle: product.handle,
        optionHandle,
        label,
        imageClass,
        swatchStyle,
        numericValue,
        isStringValue
      });

      // Mark this option as added
      addedOptions.add(optionHandle);
      optionCount++;
    });

    // Sort carat-weight and width options by numeric value (ascending)
    if (this.handle === 'carat-weight' || this.handle === 'width') {
      optionsData.sort((a, b) => {
        // If both are numeric, sort by numeric value
        if (!a.isStringValue && !b.isStringValue) {
          return a.numericValue - b.numericValue;
        }
        // If one is string and one is numeric, numeric comes first
        if (a.isStringValue && !b.isStringValue) return 1;
        if (!a.isStringValue && b.isStringValue) return -1;
        // If both are strings, sort by predefined order for carat-weight
        if (a.isStringValue && b.isStringValue) {
          if (this.handle === 'carat-weight') {
            const orderA = ProductOption.CARAT_WEIGHT_ORDER.indexOf(a.label);
            const orderB = ProductOption.CARAT_WEIGHT_ORDER.indexOf(b.label);
            // If both are in the predefined order, sort by their position
            if (orderA !== -1 && orderB !== -1) {
              return orderA - orderB;
            }
            // If only one is in the order, prioritize the one in the order
            if (orderA !== -1) return -1;
            if (orderB !== -1) return 1;
            // If neither is in the order, sort alphabetically
            return a.label.localeCompare(b.label);
          } else {
            // For width, sort alphabetically
            return a.label.localeCompare(b.label);
          }
        }
        return 0;
      });
    }

    // Render options
    optionsData.forEach(option => {
      optionshtml += `
      <a href="/products/${option.productHandle}"
        role="option"
        ${ this.activeOption === option.optionHandle ? 'aria-current="true"' : '' }
        ${ this.activeOption === option.optionHandle ? 'class="active"' : '' }
        aria-label="${option.label}">
        ${ this.imageSize ? `<span style="width: ${this.imageSize}; height: ${this.imageSize}; ${option.swatchStyle || ''}" class="bg-cover bg-center bg-no-repeat ${option.swatchStyle ? 'product-option_color-swatch' : ''} ${option.imageClass}" aria-hidden="true"></span>` : '' }
        <span class="line-clamp-3 text-xs">${option.label}</span>
      </a>`;
    });

    // TODO: Remove this once we find a better way to handle origin data in the products
    if (this.handle == 'product-center-stone-name' || this.handle == 'gemstone') {
      const originOption = document.querySelector(`[data-option-origin="${this.productHandle}"]`);
      if (originOption && originHandle) {
        const originLink = originOption.querySelector('[data-origin-link]');
        if (originLink) {
          originLink.setAttribute('href', `/products/${originHandle}`);
          originLink.classList.add('loaded');
          originOption.classList.remove('hidden');
        }
      }
    }

    this.dataset.optionCount = optionCount;

    this.innerHTML = `
      <div class="product-option relative">

          <div class="product-option_wrapper">

            <div
              class="product-option_options grid grid-cols-2 relative border-l border-t border-base-border [&>a]:border-r [&>a]:border-b [&>a]:border-base-border"
              role="listbox"
              aria-labelledby="${this.labelId}">

              ${optionshtml}

            </div>

          </div>

          <button class="product-option_see-more" aria-expanded="false" aria-controls="options-wrapper">
            <span>See More</span>
            <span class="size-4 icon" aria-hidden="true">
              <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M12 17L4.11588 7.53907C3.9391 7.32693 3.96776 7.01165 4.1799 6.83486C4.39204 6.65808 4.70732 6.68674 4.8841 6.89888L12 15.4379L19.1159 6.89888C19.2927 6.68675 19.6079 6.65808 19.8201 6.83487C20.0322 7.01165 20.0609 7.32693 19.8841 7.53907L12 17Z" fill="currentColor" />
              </svg>
            </span>
          </button>

      </div>
    `;
  }

  /**
   * Calculate the visible options
   * 
   * @returns {undefined}
   */
  calcVisibleOptions() {

    // Get the visible options
    const visibleOptions = this.querySelectorAll('.product-option_options a').length;

    if (visibleOptions < this.columns) {
      this.columns = visibleOptions;
    }
  }

  /**
   * Init component events
   * 
   * @returns {undefined}
   */
  initEvents() {

    const seeMoreButton = this.querySelector('.product-option_see-more');

    if (seeMoreButton) {
        seeMoreButton.addEventListener('click', this.handleSeeMoreClick.bind(this));
    }

    // Labels wrap differently at other widths, so the row measurement has to be redone
    this.handleResize = theme.debounce.apply(() => this.checkIfNeedsSeeMore(), 200);

    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {

    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  /**
   * Measure the height the options wrapper collapses to.
   *
   * The options are laid out on a grid whose rows can have different heights,
   * because a longer label wraps onto more lines than its neighbours. Measuring
   * a fixed pixel height therefore hides part of the last visible row and shows
   * a "See More" button that reveals nothing but a few pixels of whitespace.
   * Collapsing to a whole number of rows instead keeps the button honest.
   *
   * @returns {number|null} The collapsed height, or null when every row fits
   */
  getCollapsedHeight() {

    const optionsWrapper = this.querySelector('.product-option_wrapper');
    const optionsGrid = this.querySelector('.product-option_options');

    if (!optionsWrapper || !optionsGrid) return null;

    const options = Array.from(optionsGrid.children);

    if (options.length === 0) return null;

    // Group the options into rows by their top position
    const rowTops = [];

    options.forEach(option => {
      const top = option.getBoundingClientRect().top;

      if (!rowTops.some(rowTop => Math.abs(rowTop - top) < 2)) {
        rowTops.push(top);
      }
    });

    rowTops.sort((a, b) => a - b);

    // Every row is visible, so there is nothing left to reveal
    if (rowTops.length <= ProductOption.MAX_VISIBLE_ROWS) return null;

    // Cut right where the first hidden row starts
    return Math.ceil(rowTops[ProductOption.MAX_VISIBLE_ROWS] - optionsWrapper.getBoundingClientRect().top);
  }

  /**
   * Check if the options need a see more button
   * 
   * @returns {undefined}
   */
  checkIfNeedsSeeMore() {

    const seeMoreButton = this.querySelector('.product-option_see-more');
    const optionsWrapper = this.querySelector('.product-option_wrapper');

    if (!optionsWrapper || !seeMoreButton) return;

    const collapsedHeight = this.getCollapsedHeight();
    const needsSeeMore = collapsedHeight !== null;

    this.collapsedHeight = collapsedHeight;

    const isExpanded = optionsWrapper.classList.contains('expanded');

    requestAnimationFrame(() => {

      if (!needsSeeMore) {
        optionsWrapper.style.height = 'auto';
        optionsWrapper.style.overflow = 'visible';
        return;
      }

      optionsWrapper.style.overflow = 'hidden';
      optionsWrapper.style.height = isExpanded ? `${optionsWrapper.scrollHeight}px` : `${collapsedHeight}px`;
    });

    seeMoreButton.classList.toggle('visible', needsSeeMore);

    if (needsSeeMore) {
      optionsWrapper.id = 'options-wrapper';
    } else {
      // Back to a single state: collapsed, with no button to expand it
      optionsWrapper.removeAttribute('id');
      optionsWrapper.classList.remove('expanded');
      seeMoreButton.setAttribute('aria-expanded', 'false');
      seeMoreButton.querySelector('span:first-child').textContent = 'See More';
      seeMoreButton.querySelector('.icon').style.transform = '';
    }
  }

  /**
   * Handle see more button click
   * 
   * @param {Event} event - Click event
   * @returns {undefined}
   */
  handleSeeMoreClick(event) {
    event.preventDefault();
    this.toggleSeeMore();
  }

  /**
   * Toggle see more options
   * 
   * @returns {undefined}
   */
  toggleSeeMore() {

    const optionsWrapper = this.querySelector('.product-option_wrapper');
    const seeMoreButton = this.querySelector('.product-option_see-more');

    if (!optionsWrapper || !seeMoreButton) return;

    const isExpanded = optionsWrapper.classList.contains('expanded');
    const arrow = seeMoreButton.querySelector('.icon');
    const textSpan = seeMoreButton.querySelector('span:first-child');

    if (isExpanded) {
        // Collapse
        const currentHeight = optionsWrapper.scrollHeight;
        requestAnimationFrame(() => {
            // First set the current height explicitly
            optionsWrapper.style.height = `${currentHeight}px`;
            
            // Force a reflow
            optionsWrapper.offsetHeight;
            
            // Then animate to the collapsed height
            optionsWrapper.style.height = `${this.collapsedHeight}px`;
        });
        
        arrow.style.transform = '';
        textSpan.textContent = 'See More';
    } else {
        // Expand
        const targetHeight = optionsWrapper.scrollHeight;
        requestAnimationFrame(() => {
            optionsWrapper.style.height = `${targetHeight}px`;
        });
        
        arrow.style.transform = 'rotate(180deg)';
        textSpan.textContent = 'See Less';
    }

    optionsWrapper.classList.toggle('expanded', !isExpanded);
    seeMoreButton.setAttribute('aria-expanded', (!isExpanded).toString());
  }
}

if (!customElements.get('product-option')) {
  customElements.define('product-option', ProductOption);
}
